import {Injectable} from '@angular/core';
import {Offer} from '../../models/offer/offer.model';
import {Subject} from 'rxjs/Rx';
import {OfferNumberService} from '../../../shared/services/offer-number.service';
import {MetricsService} from '../../../shared/services/metrics.service';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {OfferMetricDataModel} from '../../../shared/components/models/metrics/offer-metric-data.model';
import {OfferService} from './offer.service';
import {ConfigurationService} from './configuration/configuration.service';
import {CoverageGroup} from '../../configuration/oc-configuration';
import {NavigateManagerService} from '../navigate-manager.service';
import {Ga4CustomEventsHelper} from '../../utils/ga4-custom-events-helper';
import {isNext} from '../../utils/utils';
import {
    OfferCoveragesGroupComparisonData
} from '../../../shared/components/dialogs/offers-comparison-dialog/offers-comparison-dialog.component';
import {LanguageService} from '../../../shared/services/language.service';

@Injectable({
    providedIn: 'root'
})
export class OffersPresentationService {

    isNext = isNext();
    private data: OffersPresentationData;
    private modeConfig: OffersPresentationModeConfig;

    private readonly MODES_CONFIG: OffersPresentationModeConfig[] = [
        {
            mode: OffersPresentationMode.OFFER_DETAILS,
            dialogTitle: 'OFFER_SECTION.offerDetails',
            sendOfferNumberEnabled: !this.isNext,
        },
        {
            mode: OffersPresentationMode.OFFERS_COMPARISON,
            dialogTitle: 'OFFER_SECTION.offersComparison',
            sendOfferNumberEnabled: false,
        }
    ];

    get isDetailsMode(): boolean {
        return this.data.mode === OffersPresentationMode.OFFER_DETAILS;
    }

    get isComparisonMode(): boolean {
        return this.data.mode === OffersPresentationMode.OFFERS_COMPARISON;
    }

    get sendOfferNumberEnabled(): boolean {
        return this.modeConfig ? this.modeConfig.sendOfferNumberEnabled : false;
    }

    get dialogTitle(): string {
        return this.modeConfig ? this.modeConfig.dialogTitle : null;
    }

    get coverageGroups(): CoverageGroup[] {
        return this.configurationService.coverageGroups;
    }

    get colSpan(): number {
        return this.data.offers.length + 2;
    }

    get offers(): Offer[] {
        return this.data.offers;
    }

    get removeOfferVisible(): boolean {
        return this.data.offers.length > 1;
    }

    get offersAmount(): number {
        return this.data.offers.length;
    }

    get buyOnlineButtonVisible(): boolean {
        return this.navigateManager.isSecondStep;
    }

    get hasQuotationId(): boolean {
        return !!this.offerNumberService.getQuotationId();
    }

    constructor(private offerNumberService: OfferNumberService,
                private offerService: OfferService,
                public navigateManager: NavigateManagerService,
                private configurationService: ConfigurationService,
                private metricsService: MetricsService,
                private languageService: LanguageService) {
    }

    init(data: OffersPresentationData): void {
        this.data = data;
        this.modeConfig = this.MODES_CONFIG.find(config => config.mode === this.data.mode);
        if (this.sendOfferNumberEnabled) {
            this.offerNumberService.offer = this.data.offers[0];
        }
    }

    selectOffer(offer: Offer): void {
        Ga4CustomEventsHelper.buyOnlineFromPresentationEvent.emit();
        if (this.isOfferSelectionNotAllowed(offer)) {
            const missingPersonalDataParams = this.offerService.getMissingPersonalDataParams(offer.id);
            this.navigateManager.openMissingPersonalDataDialog(missingPersonalDataParams);
        }
    }

    removeOffer(offerId: string): void {
        const offerIndex = this.data.offers.findIndex(offer => offer.id === offerId);
        this.metricsService.sendSimpleMetric(CustomMetricsName.REMOVE_OFFER_FROM_COMPARISON, MetricsType.BUTTON, JSON.stringify(this.getRemoveOfferMetricValue(offerId)));
        this.data.offers.splice(offerIndex, 1);
    }

    isOfferSelectionNotAllowed(offer: Offer): boolean {
        const missingPersonalDataParams = this.offerService.getMissingPersonalDataParams(offer.id);
        return !!missingPersonalDataParams.length || this.offerService.isEmailAddressMissingAndRequired();
    }

    getCoveragesComparisonData(): OfferCoveragesGroupComparisonData[] {
        return this.coverageGroups.map(group => ({
            label: this.languageService.getTranslationForKey(group.groupNames, 'name'),
            coverages: group.coverages.map(coverage => ({
                code: coverage.code,
                label: this.languageService.getTranslationForKey(coverage.labels, 'label'),
                tooltip: this.languageService.getTranslationForKey(coverage.labels, 'tooltip')
            }))
        }));
    }

    private getRemoveOfferMetricValue(offerId: string): OfferMetricDataModel {
        return {
            offerId: offerId,
            calculationId: this.offerService.calculationId,
            processId: this.offerService.processId
        };
    }
}


export interface OffersPresentationData {
    offers: Offer[];
    mode: OffersPresentationMode;
    selectOfferEvent: Subject<Offer>;
    fromOffersStep: boolean;
}

export enum OffersPresentationMode {
    OFFERS_COMPARISON = 'OFFERS_COMPARISON',
    OFFER_DETAILS = 'OFFER_DETAILS',
}

export interface OffersPresentationModeConfig {
    mode: OffersPresentationMode;
    dialogTitle: string;
    sendOfferNumberEnabled: boolean;
}
